(function () {
    'use strict';

    angular
        .module('salesflare')
        .service('joditService', joditService);

    function joditService($rootScope, $compile, $document, $window, $mdDialog, $mdPanel, $timeout, utils, templatesService, flagsService, themeService, $translate) {

        this.getJoditEditor = getJoditEditor;
        this.replaceCurrentMessage = replaceCurrentMessage;

        ////////////////////////

        /**
         * Gives you a preconfigured instance of a Jodit editor attached to the DOM
         *
         * @param {Object} joditOptions
         * @param {String} joditOptions.queryStringToBindTo
         * @param {String} [joditOptions.placeholder=Type an email]
         * @param {Object} joditOptions.previewButton
         * @param {Boolean} [joditOptions.previewButton.enabled=false]
         * @param {function(Jodit):void} [joditOptions.previewButton.execute]
         * @param {Object} joditOptions.templatesButton
         * @param {Boolean} [joditOptions.templatesButton.enabled=false]
         * @param {Object} joditOptions.attachmentsButton
         * @param {Boolean} [joditOptions.attachmentsButton.enabled=false]
         * @param {function(Array.<File>):void} [joditOptions.attachmentsButton.onAttachments]
         * @param {Object} joditOptions.mergeFieldButton
         * @param {Boolean} [joditOptions.mergeFieldButton.enabled=false]
         * @param {Array<Object>} [joditOptions.mergeFieldButton.mergeFields]
         * @param {Object} joditOptions.sendTestButton
         * @param {Boolean} [joditOptions.sendTestButton.enabled=false]
         * @param {Array<Object>} [joditOptions.sendTestButton.execute]
         * @param {Object} joditOptions.assistButton
         * @param {Boolean} [joditOptions.assistButton.enabled=false]
         * @param {function(Object):void} [joditOptions.assistButton.onAssistActionSelected]
         * @param {Boolean} [joditOptions.assistButton.writeInstructionsRequired=true]
         * @param {function(String):void} onBodyChange
         * @param {function(('html' | 'editor')):void} onModeChange
         * @returns {Jodit}
         */
        function getJoditEditor(joditOptions, onBodyChange, onModeChange) {

            let theme = themeService.get();

            let joditTheme = 'ace/theme/chrome';

            if (theme === 'dark') {
                joditTheme = 'ace/theme/github_dark';
            }

            const prefersColorSchemeDarkMediaQuery = $window.matchMedia('(prefers-color-scheme: dark)');
            function onPreferredColorSchemeChange() {
                theme = themeService.get();

                joditTheme = 'ace/theme/chrome';

                if (theme === 'dark') {
                    joditTheme = 'ace/theme/github_dark';
                }

                let aceEditor;

                // Looping over all .ace_editor elements because in workflows multiple can be used at once
                $document[0].querySelectorAll('.ace_editor').forEach(function (elem) {
                    aceEditor = ace.edit(elem);
                    aceEditor.setTheme(joditTheme);
                });
            }

            prefersColorSchemeDarkMediaQuery.addEventListener('change', onPreferredColorSchemeChange);

            const joditConfig = {
                autofocus: joditOptions.autofocus ? true : false,
                uploader: {
                    insertImageAsBase64URI: true
                },
                imageProcessor: {
                    replaceDataURIToBlobIdInView: false
                },
                allowResizeY: false,
                toolbarSticky: false,
                showCharsCounter: false,
                showWordsCounter: false,
                showXPathInStatusbar: false,
                useExtraClassesOptions: false,
                askBeforePasteFromWord: false,
                askBeforePasteHTML: false,
                processPasteHTML: true,
                disablePlugins: 'mobile,xpath,stat,search,image-properties',
                height: '100%',
                buttons: `formatting${joditOptions.attachmentsButton.enabled ? ',|,attachment' : ''},|,link${joditOptions.templatesButton.enabled ? ',|,template' : ''}${joditOptions.mergeFieldButton.enabled ? ',|,mergeField' : ''}${(joditOptions.assistButton && joditOptions.assistButton.enabled && flagsService.get('emailAI')) ? ',|,assist' : ''},|,source${joditOptions.previewButton.enabled ? ',|,preview' : ''}${joditOptions.sendTestButton.enabled ? ',|,sendTest' : ''}`,
                activeButtonsInReadOnly: ['Preview'],
                showTooltip: true,
                useNativeTooltip: true,
                colorPickerDefaultTab: 'color',
                placeholder: joditOptions.placeholder || (joditOptions.templatesButton.enabled ? $translate.instant('Type an email or use # to insert a template') : $translate.instant('Type an email')),
                enter: 'DIV',
                spellcheck: true,
                dialog: { // Doesn't seem to work atm
                    resizable: false,
                    draggable: false,
                    buttons: [
                        'dialog.close'
                    ],
                    removeButtons: []
                },
                cleanHTML: {
                    timeout: 300,
                    removeEmptyElements: true,
                    fillEmptyParagraph: true,
                    replaceNBSP: true,
                    cleanOnPaste: false, // Is false as it would otherwise clean pasted html too much
                    replaceOldTags: {
                        i: 'em',
                        b: 'strong'
                    },
                    allowTags: false,
                    denyTags: {
                        style: true,
                        script: true
                    }
                },
                image: {
                    editClass: false,
                    editStyle: false
                },
                link: {
                    formTemplate: () => `
                    <form class="jodit-ui-form" dir="auto">
                        <div class="jodit-ui-block jodit-ui-block_align_left jodit-ui-block_size_middle">
                            <div class="jodit-ui-input jodit-ui-block__url jodit-ui-input_focused_true">
                                <span class="jodit-ui-input__label">URL</span>
                                <div class="jodit-ui-input__wrapper">
                                    <input id="url_input" class="jodit-ui-input__input" dir="auto" name="url" type="text"
                                           data-ref="url_input" ref="url_input" required="true" placeholder="https://">
                                </div>
                            </div>
                        </div>
                        <div class="jodit-ui-block jodit-ui-block_align_left jodit-ui-block_size_middle" data-ref="content_input_box"
                             ref="content_input_box">
                                <div class="jodit-ui-input jodit-ui-block__content">
                                    <span class="jodit-ui-input__label">${$translate.instant('Text')}</span>
                                    <div class="jodit-ui-input__wrapper"><input id="content_input" class="jodit-ui-input__input" dir="auto" name="content"
                                         type="text" data-ref="content_input" ref="content_input" placeholder="">
                                     </div>
                                </div>
                        </div>
                        <div class="jodit-ui-block jodit-ui-block_align_full jodit-ui-block_size_middle">
                            <button class="jodit-ui-button jodit-ui-button_size_middle jodit-ui-button_unlink jodit-ui-button_variant_default jodit-ui-button_text-icons_true jodit-ui-block__unlink"
                                    type="button" role="button" aria-pressed="false" data-ref="unlink" ref="unlink" style="display: none;">
                                    <span class="jodit-ui-button__icon"></span>
                                    <span class="jodit-ui-button__text">${$translate.instant('Unlink')}</span>
                            </button>
                            <button class="jodit-ui-button jodit-ui-button_size_middle jodit-ui-button_insert jodit-ui-button_variant_primary jodit-ui-button_text-icons_true jodit-ui-block__insert"
                                    type="submit" onclick="fixUrl();" role="button" aria-pressed="false" data-ref="insert" ref="insert">
                                <span class="jodit-ui-button__icon"></span>
                                <span class="jodit-ui-button__text">${$translate.instant('Insert')}</span>
                            </button>
                        </div>
                    </form>
                    `,
                    followOnDblClick: false,
                    noFollowCheckbox: false,
                    openInNewTabCheckbox: false,
                    modeClassName: false,
                    processVideoLink: false
                },
                sourceEditorNativeOptions: {
                    showGutter: false,
                    theme: joditTheme,
                    mode: 'ace/mode/html',
                    wrap: true,
                    highlightActiveLine: true
                },
                controls: {
                    textsize: {
                        icon: 'fontsize',
                        command: 'fontsize',
                        exec: function (editor, current, execOptions) {

                            if (!execOptions.control.args) {
                                return false;
                            }

                            return editor.execCommand(execOptions.control.command, false, execOptions.control.args[0]);
                        },
                        list: {
                            '10': $translate.instant('Small'),
                            '13': $translate.instant('Normal'),
                            '18': $translate.instant('Large'),
                            '32': $translate.instant('Huge')
                        },
                        childTemplate: function (editor, key, value) {
                            return '<span class="jodit_list_element" style="font-size:' + key + 'px; line-height: 1;">' + value + '</span>';
                        },
                        tooltip: $translate.instant('Format text size')
                    }
                },
                sourceEditorCDNUrlsJS: ['https://cdn.jsdelivr.net/npm/ace-builds@1.43.6/src-min-noconflict/ace.min.js', 'https://cdn.jsdelivr.net/npm/ace-builds@1.43.6/src-min-noconflict/theme-github_dark.js'],
                beautifyHTMLCDNUrlsJS: ['https://cdn.jsdelivr.net/npm/js-beautify@1.13.0/js/lib/beautify-html.min.js']
            };

            // The paste handler expects there to be at least 1 handler.
            // Since we turn of the only internal one with `cleanOnPaste: false`
            // This means we need to at least create a dummy one here that does nothing but let the handler function properly
            // In our case however we will actually clean the HTML ourselves in this handler
            joditConfig.events = joditConfig.events || {};
            joditConfig.events.processPaste = function (event, html) {

                const purifiedBody = DOMPurify.sanitize(html);
                return purifiedBody;
            };

            joditConfig.getIcon = function (name, clearName) {

                switch (clearName) {
                    case 'brush':
                        return '<md-icon class="icon-16 md-font material-icons sf-icon-text-format"></md-icon>';
                    case 'eraser':
                        return '<md-icon class="icon-16 md-font material-icons sf-icon-clear-format"></md-icon>';
                }
            };

            if (joditOptions.mergeFieldButton.enabled) {
                if (!joditOptions.mergeFieldButton.mergeFields) {
                    throw new Error('`mergeFields` is required when `addMergeFieldButton === true`');
                }

                // Set up merge field button
                joditConfig.controls.mergeField = {
                    controlName: 'Merge field',
                    name: 'Merge field',
                    tooltip: $translate.instant('Merge fields'),
                    // eslint-disable-next-line unicorn/prefer-object-from-entries
                    list: joditOptions.mergeFieldButton.mergeFields.reduce(function (map, mergeField) {

                        mergeField.display_name = $translate.instant(mergeField.display_name);

                        const temp = {};
                        if (mergeField.api_field) {
                            temp[mergeField.api_field] = mergeField.display_name;
                        }
                        else if (mergeField.subheader) {
                            temp['subheader_' + mergeField.subheader] = mergeField.subheader;
                        }

                        return angular.merge({}, map, temp);
                    }, {}),
                    exec: function (jodit, current, execOptions) {

                        if (!execOptions.control.args) {
                            return false;
                        }

                        const apiField = execOptions.control.args[0];

                        return jodit.selection.insertHTML('{{ ' + apiField + ' | fallback="REPLACE_THIS" }}');
                    },
                    childTemplate: function (jodit, key, value) {

                        return '<div disabled="' + key.startsWith('subheader_') + '">' + value + '</div>';
                    }
                };
            }

            if (joditOptions.sendTestButton.enabled) {
                if (!joditOptions.sendTestButton.execute) {
                    throw new Error('`execute` is required when `sendTestButton.enabled === true`');
                }

                // Set up send test button
                joditConfig.controls.sendTest = {
                    controlName: $translate.instant('Send test'),
                    name: $translate.instant('Send test'),
                    tooltip: $translate.instant('Send test'),
                    exec: function () {

                        return joditOptions.sendTestButton.execute();
                    }
                };
            }

            if (joditOptions.previewButton.enabled) {
                // Set up preview button
                joditConfig.controls.preview = {
                    controlName: 'Preview',
                    name: 'Preview',
                    tooltip: $translate.instant('Preview'),
                    exec: function (jodit) {
                        const buttonIcon = angular.element(jodit.container).find('.jodit-toolbar-button_Preview button');
                        if (buttonIcon.text() === $translate.instant('Preview')) {
                            buttonIcon.text($translate.instant('Edit'));
                        }
                        else {
                            buttonIcon.text($translate.instant('Preview'));
                        }

                        return joditOptions.previewButton.execute(jodit);
                    },
                    isDisabled: function (jodit) {
                        return jodit.getMode() === Jodit.MODE_SOURCE;
                    }
                };
            }

            if (joditOptions.attachmentsButton.enabled) {
                joditConfig.controls.attachment = {
                    controlName: 'Attachment',
                    name: 'Attachment',
                    tooltip: $translate.instant('Attachment'),
                    exec: function (jodit, current, execOptions) {

                        /**
                         * Jodit adds a touchend handler on the container li element of our button
                         * this handler does a .preventDefault which breaks our handling on touch devices.
                         * In other words on touch devices the file picker doesn't show up.
                         *
                         * What we do here is detect whether the button is being clicked via a touch event and if so send out a new event
                         * to trigger the file picker.
                         * We can't do this for mouse clicks as this would trigger the file picker twice.
                         */
                        if (execOptions.originalEvent.type === 'touchend') {
                            execOptions.button.firstElementChild.click();
                        }
                    }
                };
            }

            if (joditOptions.templatesButton.enabled) {
                joditConfig.controls.template = {
                    controlName: 'Template',
                    name: 'Template',
                    tooltip: $translate.instant('Templates'),
                    exec: function (jodit, current, execOptions) {

                        /**
                         * Jodit adds a touchend handler on the container li element of our button
                         * this handler does a .preventDefault which breaks our handling on touch devices.
                         * In other words on touch devices the template panel doesn't show up.
                         *
                         * What we do here is detect whether the button is being clicked via a touch event and if so send out a new event
                         * to trigger the template panel.
                         * We can't do this for mouse clicks as this would trigger the template panel twice.
                         */
                        if (execOptions.originalEvent.type === 'touchend') {
                            execOptions.button.firstElementChild.click();
                        }
                    }
                };
            }

            // Assist button
            joditConfig.controls.assist = {
                controlName: 'Assist',
                name: 'Assist',
                tooltip: $translate.instant('Assist'),
                exec: function (jodit, current, execOptions) {

                    // Handle touch devices similar to other custom buttons
                    if (execOptions && execOptions.originalEvent && execOptions.originalEvent.type === 'touchend') {
                        execOptions.button.firstElementChild.click();

                    }
                }
            };

            joditConfig.controls.formatting = {
                controlName: 'Formatting',
                name: 'Formatting',
                tooltip: $translate.instant('Formatting'),
                exec: function (jodit, current, execOptions) {

                    /**
                     * Jodit adds a touchend handler on the container li element of our button
                     * this handler does a .preventDefault which breaks our handling on touch devices.
                     * In other words on touch devices the formatting toolbar doesn't show up.
                     *
                     * What we do here is detect whether the button is being clicked via a touch event and if so send out a new event
                     * to trigger the formatting toolbar.
                     * We can't do this for mouse clicks as this would trigger the formatting toolbar twice.
                     */
                    if (execOptions.originalEvent.type === 'touchend') {
                        execOptions.button.firstElementChild.click();
                    }
                }
            };

            const joditInstance = new Jodit(joditOptions.queryStringToBindTo, joditConfig);
            const originalDestruct = joditInstance.destruct.bind(joditInstance);
            let isDestructed = false;
            const disposableScopes = [];

            function registerScope(scope) {
                disposableScopes.push(scope);
                return scope;
            }

            if (joditOptions.maxHeight) {
                joditInstance.editor.style.maxHeight = joditOptions.maxHeight;
            }

            // Focus editor when clicking on container but not editor
            joditInstance.workplace.addEventListener('click', function (evt) {

                // If we are in source mode select the source editor
                if (joditInstance.getMode() === Jodit.MODE_SOURCE && !$document[0].activeElement.classList.contains('ace_text-input')) {
                    // // Take the .ace_editor within the element that triggered the event because in workflows multiple can be used at once
                    const aceEditor = ace.edit(evt.target.querySelector('.ace_editor'));

                    aceEditor.focus();
                    return aceEditor.navigateFileEnd();
                }
                else if (evt.target === evt.currentTarget) {
                    joditInstance.editor.focus();

                    // Put caret at the end of the text
                    const range = $document[0].createRange();
                    range.selectNodeContents(joditInstance.editor);
                    range.collapse(false);
                    const sel = $window.getSelection();
                    sel.removeAllRanges();
                    sel.addRange(range);
                }
            });

            // The editor supports dropping images but since our special layout (see supporting files/scrollable/...)
            // It wouldn't support dropping files on the container instead of the smaller editor
            // Here we basically catch the drop on the container and send it to the editor to handle the drop
            joditInstance.container.addEventListener('drop', function (evt) {

                evt.preventDefault();
                evt.stopPropagation();

                const event = new Event('drop');
                event.dataTransfer = evt.dataTransfer;

                joditInstance.container.click();
                joditInstance.editor.dispatchEvent(event);
            });
            // Make sure the browser knows that the container is a drop target in all cases, otherwise the drop event doesn't fire in all cases
            // See https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/Drag_operations#droptargets
            joditInstance.container.addEventListener('dragover', function (evt) {

                // Needs a every.call since dataTransfer.items is not a real array but a DataTransferList
                const allFilesAreImages = Array.prototype.every.call(evt.dataTransfer.items, function (i) {

                    return i.type.includes('image');
                });

                // Only stop the event when we want to handle it ourselves
                // ergo when 1 or more images
                if (allFilesAreImages) {
                    evt.preventDefault();
                    evt.stopPropagation();
                }
            });

            // Overwrite the min height every time jodit tries to set it since it messes with our layout
            joditInstance.events.on('setMinHeight', function () {

                joditInstance.container.style.minHeight = '';
                joditInstance.editor.style.minHeight = '';
            });

            // Listen on image events to make sure we don't exceed the max size limit
            joditInstance.events.on('afterInsertImage', function (image) {

                if (joditInstance.getEditorValue().length > (15 * 1000 * 1000)) {
                    image.remove();
                    return utils.showErrorToast($translate.instant('Adding this image will exceed the maximum total email size of 15MB'));
                }
            });

            if (onBodyChange) {
                joditInstance.events.on('change', function (bodyText) {

                    return $rootScope.$applyAsync(function () {

                        return onBodyChange(bodyText);
                    });
                });
            }

            joditInstance.events.on('beforeSetValueToEditor', function (html) {

                if (!html) {
                    return;
                }

                const purifiedBody = DOMPurify.sanitize(html);
                return purifiedBody;
            });


            joditInstance.events.on('afterSetMode', function () {

                angular.element(joditInstance.container).find('.jodit-toolbar-button_source button').text((joditInstance.getMode() === Jodit.MODE_SOURCE) ? $translate.instant('Hide HTML') : $translate.instant('View HTML'));

                if (onModeChange) {
                    return $rootScope.$applyAsync(function () {

                        switch (joditInstance.getMode()) {
                            case Jodit.MODE_SOURCE:

                                return onModeChange('html');
                            case Jodit.MODE_WYSIWYG:

                                return onModeChange('editor');
                        }
                    });
                }
            });

            // Prevent Jodit's Enter plugin from copying special Salesflare classes (.sf-message, .sf-signature) when splitting blocks
            joditInstance.events.on('beforeEnter', function () {

                // Return `true` in all cases to prevent enter plugin without preventing the default behavior
                // If we don't do this then pressing enter won't select a template when doing a # template search but just insert an enter in the jodit editor
                try {
                    const sel = joditInstance.selection && joditInstance.selection.sel;
                    if (!sel || sel.rangeCount === 0) {
                        return true;
                    }

                    const anchorNode = sel.anchorNode;
                    if (!anchorNode) {
                        return true;
                    }

                    const element = anchorNode.nodeType === 1 ? anchorNode : anchorNode.parentElement;
                    if (!element) {
                        return true;
                    }

                    // Find the nearest block element. Jodit clones classes from this when pressing Enter.
                    const block = element.closest('div, p, li, h1, h2, h3, h4, h5, h6');
                    if (!block) {
                        return true;
                    }

                    const originalClass = block.getAttribute('class');

                    if (!originalClass || (!originalClass.includes('sf-message') && !originalClass.includes('sf-signature'))) {
                        return true;
                    }

                    // Temporarily remove classes so the Enter created block won't inherit them
                    block.removeAttribute('class');

                    // Restore on the original node after Enter completes
                    $timeout(function () {
                        if (block && !block.getAttribute('class') && originalClass) {
                            block.setAttribute('class', originalClass);
                        }
                    }, 0);
                }
                catch {
                    // Fail open to not interfere with Enter behavior
                }

                return true;
            });

            const formattingToolbarButtonTemplate = ''
                + '<button class="jodit-toolbar-button__button" ng-click="vm.showFormattingPanel($event)" type="button">'
                + '     <md-icon md-font-icon="sf-icon-text-format" class="icon-16"></md-icon>'
                + '</button>';
            const formattingToolbarButtonScope = registerScope($rootScope.$new());
            formattingToolbarButtonScope.vm = {
                showFormattingPanel: function (event) {

                    // If panel is already opened, close panel on click
                    if (joditInstance.formattingPanelRef) {
                        return joditInstance.formattingPanelRef.close().then(function () {

                            joditInstance.formattingPanelRef.destroy();
                            joditInstance.formattingPanelRef = undefined;
                        });
                    }

                    // Position the panel above the toolbar:
                    // The easiest way is to attach the panel to the document body and then position the panel relative to the toolbar container.
                    // The problem with that solution is that the panel stays open when the editor is removed from the DOM since the scope of document body is never destroyed.
                    // For that reason we attach the panel to the jodit toolbar. The problem now is that the jodit elements are positioned relatively which causes the mdPanel calculations to fail.
                    // We overlap the toolbar with the panel and then set negative top margin to align the panel above the toolbar.
                    const position = $mdPanel.newPanelPosition()
                        .absolute()
                        .bottom(((angular.element(joditInstance.toolbar.container).height() + 2) + 'px')) // The +2px is an offset to make up for the box shadow of the panel
                        .left('8px'); // Align panel more the icon of the first button of the toolbar

                    $mdPanel.open({
                        attachTo: angular.element(joditInstance.container),
                        position,
                        clickOutsideToClose: false,
                        openFrom: event,
                        template:
                            '<div class="formatting-panel-toolbar-container-' + joditInstance.id + ' md-whiteframe-3dp"></div>',
                        propagateContainerEvents: true,
                        onOpenComplete: function (mdPanelRef) {

                            mdPanelRef[0].panelEl[0].style.position = 'absolute';

                            // Keep a reference to the panel so we can close it when necessary
                            joditInstance.formattingPanelRef = mdPanelRef[0];

                            // We suffix the class name with the id of the jodit instance (timestamp of creation) so nothing weird happens when multiple jodit editors are open at once.
                            const formattingPanelToolbarContainerClassName = '.formatting-panel-toolbar-container-' + joditInstance.id;

                            // Build the toolbar and insert it in the formatting panel container.
                            new Jodit.modules.ToolbarCollection(joditInstance).build(['textsize', 'font', '|', 'bold', 'underline', 'italic', 'brush', '|', 'table', 'ul', 'ol', 'align', '|', 'eraser']).appendTo(angular.element(formattingPanelToolbarContainerClassName)[0]);
                        }
                    });
                }
            };

            // We need to wait for the next tick to be able to start modifying the dom
            // Since 3.3.8 the plugin system changed and became async https://github.com/xdan/jodit/compare/3.3.7...3.3.8?w=1#diff-70ea7a6a97e1a4e20f3beee88a8df185R66
            $timeout(function () {

                const linkScript = $document[0].createElement('script');
                linkScript.text = `function fixUrl() {

                    var contentInput = document.getElementById('content_input');
                    var urlInput = document.getElementById('url_input');
                    contentInput.value = contentInput.value?contentInput.value:urlInput.value;
                    urlInput.value = urlInput.value.replace(/^(?!https?:\\/\\/)(.*)/, 'https://$1');
                };`;
                $document[0].body.append(linkScript);

                // Use the container as reference for dom manipulation.
                // This is important when having more than 1 instance in the dom
                // We want to make sure we are only touching our current instance
                const container = angular.element(joditInstance.container);

                const formattingToolbarButton = $compile(formattingToolbarButtonTemplate)(formattingToolbarButtonScope);
                container.find('.jodit-toolbar-button_Formatting button').replaceWith(formattingToolbarButton);

                if (joditOptions.attachmentsButton.enabled) {
                    // We create our own compiled button here to get the file upload working out of the box
                    const attachmentToolbarButtonTemplate = ''
                        + '<button class="jodit-toolbar-button__button" ngf-pattern="" ngf-select ngf-change="vm.onAttachments($files)" accept="" ngf-multiple="true" type="button">'
                        + '     <md-icon md-font-icon="sf-icon-attachment" class="icon-16"></md-icon>'
                        + '</button>';
                    const attachmentToolbarButtonScope = registerScope($rootScope.$new());
                    attachmentToolbarButtonScope.vm = {
                        onAttachments: joditOptions.attachmentsButton.onAttachments
                    };

                    const attachmentToolbarButton = $compile(attachmentToolbarButtonTemplate)(attachmentToolbarButtonScope);
                    container.find('.jodit-toolbar-button_Attachment button').replaceWith(attachmentToolbarButton);
                }

                if (joditOptions.mergeFieldButton.enabled) {
                    const innerTemplate = `
                                        <md-icon style="margin: 0 4px 0 0" md-font-icon="sf-icon-merge-fields" class="icon-16"></md-icon>
                                        <span>${$translate.instant('Merge fields')}</span>
                                    `;
                    // We need to compile the inner template for the md-icon to properly work
                    container.find('.jodit-toolbar-button_Merge_field button').empty().append($compile(innerTemplate)(registerScope($rootScope.$new())));
                }

                if (joditOptions.templatesButton.enabled) {
                    angular.element(joditInstance.editor).textcomplete([{
                        match: /\B#(\w*)$/,
                        search: function (term, callback) {

                            const templateParams = { limit: 10000 };

                            if (term) {
                                templateParams.search = term;
                            }

                            return templatesService.get(templateParams, { ignoreLoadingBar: true }).then(function (response) {

                                return callback(response.data);
                            });
                        },
                        index: 1,
                        cache: true,
                        template: function (item) {

                            return '<md-list-item style="max-width: 450px; min-height: 48px; height: 48px;" class="md-2-line">'
                                + '    <div class="md-list-item-text">'
                                + '        <div style="font-size: 16px;" class="accent ellipsis">' + item.name + '</div>'
                                + '        <div class="ellipsis secondary">' + item.plain_text_body + '</div>'
                                + '    </div>'
                                + '</md-list-item>';
                        },
                        replace: function (item) {

                            joditOptions.templatesButton.onSelectTemplate(item, true);

                            // Since the jquery autocomplete plugin does not trigger a Jodit change event we need to fire it ourselves
                            $timeout(() => {

                                joditInstance.events.fire('change', joditInstance.getEditorValue());
                            }, 0);

                            return item.html_body;
                        }
                    }], {
                        placement: 'bottom',
                        rightEdgeOffset: 16
                    });

                    const templateToolbarButtonTemplate = ''
                        + '<button class="jodit-toolbar-button__button" ng-click="vm.onTemplates($event)" type="button">'
                        + '     <md-icon style="margin: 0 3px 0 0;" md-font-icon="sf-icon-bookmark-outline" class="icon-16"></md-icon>'
                        + '     <span>' + $translate.instant('Templates') + '</span>'
                        + '</button>';
                    const templateToolbarButtonScope = registerScope($rootScope.$new());
                    templateToolbarButtonScope.vm = {
                        onTemplates: function () {

                            const position = $mdPanel.newPanelPosition()
                                .relativeTo(container.find('.jodit-toolbar-button_Template'))
                                .addPanelPosition($mdPanel.xPosition.ALIGN_START, $mdPanel.yPosition.ABOVE);
                            // Opening the panel and dialogs remove the focus/selection in the Jodit editor
                            // We save it here so we can restore it later to insert the template at the correct position
                            let savedRange;

                            if (joditInstance.selection.isFocused()) {
                                savedRange = joditInstance.selection.sel.getRangeAt(0);
                            }

                            return templatesService.get({ limit: 10000 }, { ignoreLoadingBar: true }).then(function (response) {

                                return $mdPanel.open('templateSelectionPanel', {
                                    attachTo: angular.element($document[0].body),
                                    position,
                                    locals: {
                                        templates: response.data,
                                        onSelectTemplate: function (selectedTemplate) {

                                            if (selectedTemplate) {

                                                // Restore range to insert the template at the cursor location before opening the template panels/dialogs
                                                if (savedRange) {
                                                    joditInstance.selection.sel.removeAllRanges();
                                                    joditInstance.selection.sel.addRange(savedRange);
                                                }

                                                joditOptions.templatesButton.onSelectTemplate(selectedTemplate);
                                            }
                                        }
                                    }
                                });
                            });
                        }
                    };

                    const templateToolbarButton = $compile(templateToolbarButtonTemplate)(templateToolbarButtonScope);
                    container.find('.jodit-toolbar-button_Template button').replaceWith(templateToolbarButton);
                }

                if (joditOptions.assistButton && joditOptions.assistButton.enabled && flagsService.get('emailAI')) {
                    // Replace the default Assist button content with a compiled Angular button (icon + text)
                    const assistToolbarButtonTemplate = `
                        <button class="jodit-toolbar-button__button" ng-click="vm.onAssist($event)" type="button">
                            <md-icon style="margin: 0 3px 0 0;" md-font-icon="sf-icon-sparkles" class="icon-16"></md-icon>
                            <span>${$translate.instant('Assist')}</span>
                        </button>
                    `;
                    const assistToolbarButtonScope = registerScope($rootScope.$new());
                    assistToolbarButtonScope.vm = {
                        onAssist: function () {

                            const hasTypedMessageContent = computeHasTypedMessageContent(joditInstance);

                            const position = $mdPanel.newPanelPosition()
                                .relativeTo(container.find('.jodit-toolbar-button_Assist'))
                                .addPanelPosition($mdPanel.xPosition.ALIGN_START, $mdPanel.yPosition.ABOVE);

                            return $mdPanel.open({
                                attachTo: angular.element($document[0].body),
                                position,
                                clickOutsideToClose: true,
                                panelClass: 'assist-panel-container',
                                template:
                                    '<div class="assist-panel-container-' + joditInstance.id + ' md-whiteframe-3dp"></div>',
                                onOpenComplete: function (mdPanelRef) {

                                    const panel = mdPanelRef[0];
                                    panel.panelEl[0].style.position = 'absolute';

                                    joditInstance.assistPanelRef = panel;

                                    const assistPanelContainerClassName = '.assist-panel-container-' + joditInstance.id;

                                    const actions = [
                                        { id: 'write', label: $translate.instant('Write email'), icon: 'sf-icon-edit', tooltip: $translate.instant('Start an email from your prompt.') },
                                        { id: 'adjust', label: $translate.instant('Adjust email'), icon: 'sf-icon-adjust_email', tooltip: $translate.instant('Rewrite per your instructions.') },
                                        { id: 'correct', label: $translate.instant('Correct email'), icon: 'sf-icon-correct_email', tooltip: $translate.instant('Fix spelling, grammar, punctuation.') },
                                        { id: 'polish', label: $translate.instant('Polish email'), icon: 'sf-icon-polish_email', tooltip: $translate.instant('Improve clarity and flow.') },
                                        { id: 'formal', label: $translate.instant('Make formal'), icon: 'sf-icon-formalize_email', tooltip: $translate.instant('Use a more professional tone.') },
                                        { id: 'informal', label: $translate.instant('Make informal'), icon: 'sf-icon-informalize_email', tooltip: $translate.instant('Use a more friendly tone.') },
                                        { id: 'longer', label: $translate.instant('Make longer'), icon: 'sf-icon-extend_email', tooltip: $translate.instant('Add helpful details.') },
                                        { id: 'shorter', label: $translate.instant('Make shorter'), icon: 'sf-icon-shorten_email', tooltip: $translate.instant('Be more concise.') },
                                        { id: 'personalize', label: $translate.instant('Personalize email'), icon: 'sf-icon-personalize_email', tooltip: $translate.instant('Insert relevant CRM details.') },
                                        { id: 'translate', label: $translate.instant('Translate email'), icon: 'sf-icon-translate_email', tooltip: $translate.instant('Translate to the selected language.') }
                                    ];

                                    const innerTemplate = `
                                        <md-list class="assist-actions-list" style="max-height: 300px; overflow: auto;">
                                            <div ng-repeat="action in vm.actions">
                                                <md-button class="md-no-style" ng-click="vm.onSelect(action, $event)" ng-disabled="vm.shouldDisableAction(action)">
                                                    <md-icon style="margin-right: 6px;"md-font-icon="{{action.icon}}" class="icon-20"></md-icon>
                                                    <span>{{action.label}}</span>
                                                    <md-tooltip ng-if="vm.showActionTooltips || vm.shouldDisableAction(action)" md-direction="right">
                                                        {{ vm.shouldDisableAction(action) ? vm.writeEmailFirstText : action.tooltip }}
                                                    </md-tooltip>
                                                </md-button>
                                            </div>
                                        </md-list>
                                    `;

                                    // Keep full tooltip behavior on non-mobile; on mobile, only disabled actions keep a tooltip.
                                    const supportsHover = $window.matchMedia && $window.matchMedia('(hover: hover)').matches;

                                    const panelScope = registerScope($rootScope.$new());
                                    panelScope.vm = {
                                        actions,
                                        showActionTooltips: supportsHover,
                                        writeEmailFirstText: $translate.instant('Write an email first.'),
                                        shouldDisableAction: (action) => {

                                            if (action.id === 'write') {
                                                return false;
                                            }

                                            return !hasTypedMessageContent;
                                        },
                                        onSelect: async function (action, event) {

                                            const options = {};

                                            if (action.id === 'translate') {
                                                return this.openTranslatePanel(event);
                                            }
                                            else if (action.id === 'adjust' || action.id === 'write') {
                                                if (joditInstance.assistPanelRef) {
                                                    joditInstance.assistPanelRef.close();
                                                }

                                                options.instruction = await this.openInstructionsDialog(action.id);
                                            }

                                            if (joditOptions.assistButton && angular.isFunction(joditOptions.assistButton.onAssistActionSelected)) {
                                                joditOptions.assistButton.onAssistActionSelected({
                                                    action: action.id,
                                                    options
                                                });
                                            }

                                            if (joditInstance.assistPanelRef) {
                                                joditInstance.assistPanelRef.close();
                                            }
                                        }
                                    };

                                    // List of supported languages for AI translation
                                    // Based on https://cloud.google.com/translate/docs/languages#adaptive_translation
                                    panelScope.vm.languages = [
                                        { code: 'ar', label: 'Arabic', alt: ['العربية', 'Arabisch'] },
                                        { code: 'bn', label: 'Bengali', alt: ['বাংলা'] },
                                        { code: 'bg', label: 'Bulgarian', alt: ['Български'] },
                                        { code: 'ca', label: 'Catalan', alt: ['Català'] },
                                        { code: 'zh-CN', label: 'Chinese (Simplified)', alt: ['简体中文', '中文'] },
                                        { code: 'hr', label: 'Croatian', alt: ['Hrvatski'] },
                                        { code: 'cs', label: 'Czech', alt: ['Čeština'] },
                                        { code: 'da', label: 'Danish', alt: ['Dansk'] },
                                        { code: 'nl', label: 'Dutch', alt: ['Nederlands'] },
                                        { code: 'en', label: 'English', alt: [] },
                                        { code: 'et', label: 'Estonian', alt: ['Eesti'] },
                                        { code: 'fi', label: 'Finnish', alt: ['Suomi'] },
                                        { code: 'fr', label: 'French', alt: ['Français'] },
                                        { code: 'de', label: 'German', alt: ['Deutsch'] },
                                        { code: 'el', label: 'Greek', alt: ['Ελληνικά'] },
                                        { code: 'gu', label: 'Gujarati', alt: ['ગુજરાતી'] },
                                        { code: 'he', label: 'Hebrew', alt: ['עברית'] },
                                        { code: 'hi', label: 'Hindi', alt: ['हिन्दी', 'हिंदी'] },
                                        { code: 'hu', label: 'Hungarian', alt: ['Magyar'] },
                                        { code: 'is', label: 'Icelandic', alt: ['Íslenska'] },
                                        { code: 'id', label: 'Indonesian', alt: ['Bahasa Indonesia'] },
                                        { code: 'it', label: 'Italian', alt: ['Italiano'] },
                                        { code: 'ja', label: 'Japanese', alt: ['日本語'] },
                                        { code: 'kn', label: 'Kannada', alt: ['ಕನ್ನಡ'] },
                                        { code: 'ko', label: 'Korean', alt: ['한국어'] },
                                        { code: 'lv', label: 'Latvian', alt: ['Latviešu'] },
                                        { code: 'lt', label: 'Lithuanian', alt: ['Lietuvių'] },
                                        { code: 'ml', label: 'Malayalam', alt: ['മലയാളം'] },
                                        { code: 'mr', label: 'Marathi', alt: ['मराठी'] },
                                        { code: 'no', label: 'Norwegian', alt: ['Norsk'] },
                                        { code: 'fa', label: 'Persian', alt: ['فارسی'] },
                                        { code: 'pl', label: 'Polish', alt: ['Polski'] },
                                        { code: 'pt', label: 'Portuguese', alt: ['Português'] },
                                        { code: 'pa', label: 'Punjabi', alt: ['ਪੰਜਾਬੀ'] },
                                        { code: 'ro', label: 'Romanian', alt: ['Română'] },
                                        { code: 'ru', label: 'Russian', alt: ['Русский'] },
                                        { code: 'sk', label: 'Slovak', alt: ['Slovenčina'] },
                                        { code: 'sl', label: 'Slovenian', alt: ['Slovenščina'] },
                                        { code: 'es', label: 'Spanish', alt: ['Español'] },
                                        { code: 'sw', label: 'Swahili', alt: ['Kiswahili'] },
                                        { code: 'sv', label: 'Swedish', alt: ['Svenska'] },
                                        { code: 'ta', label: 'Tamil', alt: ['தமிழ்'] },
                                        { code: 'te', label: 'Telugu', alt: ['తెలుగు'] },
                                        { code: 'th', label: 'Thai', alt: ['ไทย'] },
                                        { code: 'tr', label: 'Turkish', alt: ['Türkçe'] },
                                        { code: 'uk', label: 'Ukrainian', alt: ['Українська'] },
                                        { code: 'ur', label: 'Urdu', alt: ['اردو'] },
                                        { code: 'vi', label: 'Vietnamese', alt: ['Tiếng Việt'] },
                                        { code: 'zu', label: 'Zulu', alt: [] }
                                    ];

                                    const languageMap = {};
                                    panelScope.vm.languages.forEach(function (lang) {
                                        languageMap[lang.code] = lang.label;
                                    });
                                    panelScope.vm.languageMap = languageMap;

                                    panelScope.vm.pinnedLanguageCodes = ['en', 'fr', 'es', 'it', 'de', 'pt'];

                                    const navigatorLang = ($window.navigator && ($window.navigator.language || ($window.navigator.languages && $window.navigator.languages[0]))) || 'en';
                                    const base = (navigatorLang || 'en').toLowerCase().split('-')[0];
                                    const browserLanguageCode = base === 'zh' ? 'zh-CN' : panelScope.vm.languages.find(function (l) {
                                        return l.code === base;
                                    })?.code;

                                    // Insert browser language as first in the list
                                    if (browserLanguageCode && !panelScope.vm.pinnedLanguageCodes.includes(browserLanguageCode)) {
                                        panelScope.vm.pinnedLanguageCodes = [browserLanguageCode, ...panelScope.vm.pinnedLanguageCodes];
                                    }

                                    panelScope.vm.allLanguages = [...panelScope.vm.languages];

                                    panelScope.vm.selectLanguage = (languageLabel) => {

                                        if (joditOptions.assistButton && angular.isFunction(joditOptions.assistButton.onAssistActionSelected)) {
                                            joditOptions.assistButton.onAssistActionSelected({
                                                action: 'translate',
                                                options: {
                                                    language: languageLabel
                                                }
                                            });
                                        }

                                        if (joditInstance.assistPanelRef) {
                                            return joditInstance.assistPanelRef.close();
                                        }
                                    };

                                    panelScope.vm.openInstructionsDialog = (actionId) => {

                                        const config = {
                                            dialogId: 'promptEmailAIInstructionsDialog',
                                            title: actionId === 'adjust' ? $translate.instant('Adjust email') : $translate.instant('Write email'),
                                            ariaLabel: $translate.instant('Provide instructions'),
                                            label: $translate.instant('Instructions'),
                                            formId: 'promptEmailAIInstructionsForm',
                                            required: joditOptions.assistButton.writeInstructionsRequired === false && actionId === 'write' ? false : true,
                                            inputName: 'promptEmailAIInstructions',
                                            requiredErrorMessage: $translate.instant('Instructions are required'),
                                            cancelText: $translate.instant('Cancel'),
                                            okText: $translate.instant('Generate')
                                        };

                                        return $mdDialog.show($mdDialog.sfPromptDialog(config));
                                    };

                                    panelScope.vm.openTranslatePanel = () => {

                                        if (joditInstance.translatePanelRef) {
                                            return;
                                        }

                                        const translatePanelPosition = $mdPanel.newPanelPosition()
                                            .relativeTo(joditInstance.assistPanelRef && joditInstance.assistPanelRef.panelEl ? joditInstance.assistPanelRef.panelEl : angular.element(`.assist-panel-container-${joditInstance.id}`))
                                            .addPanelPosition($mdPanel.xPosition.OFFSET_END, $mdPanel.yPosition.ALIGN_BOTTOMS);

                                        $mdPanel.open({
                                            attachTo: angular.element($document[0].body),
                                            position: translatePanelPosition,
                                            clickOutsideToClose: true,
                                            escapeToClose: true,
                                            template: `
                                                <div layout="column" id="translate-panel-${joditInstance.id}" class="translate-panel-container translate-panel-container-${joditInstance.id} md-whiteframe-3dp">
                                                    <form role="form" layout="column" class="sticky-form" novalidate>
                                                        <md-input-container>
                                                        <input class="search-input" ng-model="vm.search" aria-label="Search language">
                                                        </md-input-container>
                                                    </form>
                                                    <md-divider></md-divider>
                                                    <md-list flex>
                                                        <md-list-item ng-if="!vm.search" ng-repeat="code in vm.pinnedLanguageCodes" ng-click="vm.selectLanguage(vm.languageMap[code])">
                                                        <p style="margin:0;">{{vm.languageMap[code]}}</p>
                                                        </md-list-item>
                                                        <md-list-item ng-if="vm.search" ng-repeat="lang in vm.languages track by lang.code" ng-click="vm.selectLanguage(lang.label)">
                                                        <p style="margin: 0;">{{lang.label}}</p>
                                                        </md-list-item>
                                                    </md-list>
                                                </div>
                                            `,
                                            onOpenComplete: function (translatePanelRef) {
                                                joditInstance.translatePanelRef = translatePanelRef[0];

                                                angular.element('.search-input').attr('placeholder', $translate.instant('Search languages'));
                                            },
                                            onRemoving: function () {
                                                joditInstance.translatePanelRef = undefined;
                                            },
                                            locals: {
                                                panelVm: panelScope.vm
                                            },
                                            controller: ['$scope', 'panelVm', function ($scope, panelVm) {

                                                const vm = this;
                                                angular.extend(vm, panelVm);

                                                let thread = null;
                                                const allLanguages = (panelVm.allLanguages && [...panelVm.allLanguages]) || (panelVm.languages && [...panelVm.languages]) || [];
                                                vm.search = '';
                                                vm.languages = allLanguages;

                                                $scope.$watch(function () {
                                                    return vm.search;
                                                }, function (newValue, oldValue) {

                                                    if (newValue === oldValue) {
                                                        return;
                                                    }

                                                    $timeout.cancel(thread);

                                                    thread = $timeout(function () {

                                                        if (!vm.search) {
                                                            vm.languages = allLanguages;
                                                            return;
                                                        }

                                                        const q = vm.search.toLowerCase();

                                                        vm.languages = allLanguages.filter(function (lang) {
                                                            const inLabel = lang.label && lang.label.toLowerCase().includes(q);
                                                            const inCode = lang.code && lang.code.toLowerCase().includes(q);
                                                            const inAlt = angular.isArray(lang.alt) && lang.alt.some(function (altName) {
                                                                return altName && String(altName).toLowerCase().includes(q);
                                                            });
                                                            return inLabel || inCode || inAlt;
                                                        });
                                                    });
                                                });
                                            }],
                                            controllerAs: 'vm'
                                        });
                                    };

                                    const compiled = $compile(innerTemplate)(panelScope);
                                    angular.element(assistPanelContainerClassName).empty().append(compiled);
                                },
                                onRemoving: () => {

                                    joditInstance.assistPanelRef = undefined;

                                    if (joditInstance.translatePanelRef) {
                                        joditInstance.translatePanelRef.close().then(function () {
                                            joditInstance.translatePanelRef = undefined;
                                        });
                                    }
                                }
                            });
                        }
                    };

                    const assistToolbarButton = $compile(assistToolbarButtonTemplate)(assistToolbarButtonScope);
                    container.find('.jodit-toolbar-button_Assist button').replaceWith(assistToolbarButton);
                }

                // If we added out custom buttons make sure they have text on the button
                // Jodit does not support mixed icon/text button toolbars so yeah :D
                if (joditOptions.previewButton.enabled) {
                    container.find('.jodit-toolbar-button_Preview button').text($translate.instant('Preview'));
                }

                if (joditOptions.sendTestButton.enabled) {
                    container.find('.jodit-toolbar-button_Send_test button').text($translate.instant('Send test'));
                }

                container.find('.jodit-toolbar-button_source button').text($translate.instant('View HTML'));
            });

            joditInstance.destruct = function () {

                if (isDestructed) {
                    return;
                }

                isDestructed = true;

                prefersColorSchemeDarkMediaQuery.removeEventListener('change', onPreferredColorSchemeChange);

                if (joditOptions.templatesButton.enabled) {
                    try {
                        angular.element(joditInstance.editor).textcomplete('destroy');
                    }
                    catch {
                        // Noop
                    }
                }

                if (joditInstance.formattingPanelRef) {
                    joditInstance.formattingPanelRef.destroy();
                    joditInstance.formattingPanelRef = undefined;
                }

                if (joditInstance.assistPanelRef) {
                    joditInstance.assistPanelRef.close();
                    joditInstance.assistPanelRef = undefined;
                }

                if (joditInstance.translatePanelRef) {
                    joditInstance.translatePanelRef.close();
                    joditInstance.translatePanelRef = undefined;
                }

                disposableScopes.forEach(function (scope) {
                    if (scope) {
                        scope.$destroy();
                    }
                });

                return originalDestruct();
            };

            return joditInstance;
        }

        /**
         * Replaces the current message content with the suggested HTML while preserving signature/quoted blocks placement.
         * Inserts the suggestion before the first occurrence of `.sf-signature` or `.sf-message` if present, else replaces fully.
         *
         * @param {Jodit} joditInstance
         * @param {String} suggestedHtml
         */
        function replaceCurrentMessage(joditInstance, suggestedHtml) {

            const suggestion = suggestedHtml || '';
            const currentHtml = joditInstance.getEditorValue() || '';
            const container = $document[0].createElement('div');
            container.innerHTML = currentHtml;

            const firstSpecialBlock = container.querySelector('.sf-signature, .sf-message');

            if (firstSpecialBlock) {
                while (container.firstChild && container.firstChild !== firstSpecialBlock) {
                    container.firstChild.remove();
                }

                firstSpecialBlock.insertAdjacentHTML('beforebegin', suggestion);
            }
            else {
                container.innerHTML = suggestion;
            }

            joditInstance.setEditorValue(container.innerHTML);
        }

        /**
         * Returns true if the editor contains user-typed content
         * (excluding signature `.sf-signature` and quoted message `.sf-message` blocks).
         *
         * @param {Jodit} joditInstance
         * @returns {Boolean}
         */
        function computeHasTypedMessageContent(joditInstance) {

            const currentHtml = joditInstance.getEditorValue() || '';
            const checkContainer = $document[0].createElement('div');
            checkContainer.innerHTML = currentHtml;

            Array.prototype.forEach.call(checkContainer.querySelectorAll('.sf-signature, .sf-message'), function (node) {
                if (node && node.parentNode) {
                    node.remove();
                }
            });

            const hasTypedText = ((checkContainer.textContent || '').replace(/\u00A0/g, '').trim().length > 0);
            return hasTypedText;
        }
    }
})();
